import { supabase } from '../supabase';

/**
 * Recupera la configurazione WhatsApp (WAHA) per l'utente corrente.
 * Funzione standalone estratta dal legacy whatsappStore — nessuna dipendenza Zustand.
 * Supporta sia modalità globale che per-utente, con cache sessionStorage (5 min, per-user).
 */
export async function getUserWhatsAppConfig(): Promise<{
  config: any;
  isGlobalMode: boolean;
  organizationId: string;
}> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    const userId = userData?.user?.id;

    if (userError || !userId) {
      console.error('[getUserWhatsAppConfig] No authenticated user found:', userError);
      throw new Error('Per favore, effettua il login');
    }

    // Cache per utente
    const userSpecificCacheKey = `waha_config_cache_${userId}`;
    const cachedConfig = sessionStorage.getItem(userSpecificCacheKey);

    if (cachedConfig) {
      try {
        const parsedCache = JSON.parse(cachedConfig);
        const cacheTime = parsedCache.timestamp || 0;
        const now = Date.now();

        if (now - cacheTime < 5 * 60 * 1000) {
          if (parsedCache.userId === userId) {
            return parsedCache.data;
          } else {
            sessionStorage.removeItem(userSpecificCacheKey);
          }
        }
      } catch (e) {
        console.warn('[getUserWhatsAppConfig] Error parsing cache, will fetch fresh config:', e);
      }
    }

    // Organizzazione utente
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', userId)
      .single();

    if (profileError || !userProfile?.organization_id) {
      console.error('[getUserWhatsAppConfig] User has no organization:', profileError);
      throw new Error('Profilo utente non trovato');
    }

    // Modalità globale o per-utente
    const { data: settings } = await supabase
      .from('settings')
      .select('whatsapp_global_config')
      .eq('organization_id', userProfile.organization_id)
      .single();

    const isGlobalMode = settings?.whatsapp_global_config !== false;

    let config;

    if (isGlobalMode) {
      const { data: globalConfig, error: configError } = await supabase
        .from('api_configurations')
        .select('*')
        .eq('service_name', 'waha')
        .eq('organization_id', userProfile.organization_id)
        .eq('is_active', true)
        .is('user_id', null)
        .maybeSingle();

      if (configError) {
        console.error('[getUserWhatsAppConfig] Error fetching global WhatsApp config:', configError);
        throw new Error("Errore nell'accesso alla configurazione WhatsApp");
      }

      if (!globalConfig) {
        console.error('[getUserWhatsAppConfig] No global config found for organization');
        throw new Error('WhatsApp non configurato per questa organizzazione');
      }

      // Controlla restrizioni accesso sulla config globale
      const { data: accessRows } = await supabase
        .from('whatsapp_user_access')
        .select('user_id')
        .eq('configuration_id', globalConfig.id)
        .eq('organization_id', userProfile.organization_id);

      if (accessRows && accessRows.length > 0) {
        const hasAccess = accessRows.some(r => r.user_id === userId);
        if (!hasAccess) {
          throw new Error('Non hai accesso alla configurazione WhatsApp');
        }
      }

      config = globalConfig;
    } else {
      const { data: userAccess, error: accessError } = await supabase
        .from('whatsapp_user_access')
        .select('configuration_id')
        .eq('user_id', userId)
        .eq('organization_id', userProfile.organization_id)
        .maybeSingle();

      if (accessError) {
        console.error('[getUserWhatsAppConfig] Error fetching user WhatsApp access:', accessError);
        throw new Error("Errore nell'accesso alla configurazione WhatsApp");
      }

      if (!userAccess?.configuration_id) {
        console.error('[getUserWhatsAppConfig] User has no WhatsApp configuration assigned');
        throw new Error('Nessuna configurazione WhatsApp assegnata al tuo account');
      }

      const { data: userConfig, error: configError } = await supabase
        .from('api_configurations')
        .select('*')
        .eq('id', userAccess.configuration_id)
        .eq('is_active', true)
        .single();

      if (configError) {
        console.error('[getUserWhatsAppConfig] Error fetching user WhatsApp config:', configError);
        throw new Error('Errore nel caricamento della configurazione WhatsApp');
      }

      if (!userConfig) {
        console.error('[getUserWhatsAppConfig] Config not found for this user');
        throw new Error('Configurazione WhatsApp non trovata');
      }

      config = userConfig;
    }

    const result = {
      config,
      isGlobalMode,
      organizationId: userProfile.organization_id,
    };

    sessionStorage.setItem(
      userSpecificCacheKey,
      JSON.stringify({
        data: result,
        timestamp: Date.now(),
        userId,
      })
    );

    return result;
  } catch (error) {
    console.error('[getUserWhatsAppConfig]', error);
    const errorMessage = error instanceof Error ? error.message : 'Errore nella configurazione WhatsApp';
    throw new Error(errorMessage);
  }
}
